import React, { useEffect, useMemo, useState } from 'react';
import { bindActionCreators } from 'redux';
import { store } from 'redux-rest-reducer';
import { connect } from 'react-redux';
import { TableRow, Input, Checkbox } from 'semantic-ui-react';

import { Row, Separator } from '@cuemath/comps';
import styled, { keyframes } from 'styled-components';

import { get as getConfig } from 'actions/configs';

import { AppProvider } from '../../app-context';
import { REFERRAL_CAMPAIGNS } from 'constants/api';
import COLORS from 'constants/colors';
import { Heading, Table, TableCell, TableInfoCell, Drawer } from 'comps';
import { getFormatedDate } from 'utils';

const FadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const Root = styled.div`
  padding: 24px 16px;
  animation: ${FadeIn} 0.5s ease-in-out;
`;

const getModifiedCampaigns = campaigns => {
  return campaigns?.map(campaign => {
    const { expiration_date: expirationDate, is_active: isActive } = campaign || {};
    const activeCampaign =
      isActive && +new Date(expirationDate) / 1000 >= Date.now() / 1000;
    return { ...campaign, state: activeCampaign ? 'active' : 'inActive' };
  });
};

const ReferralCampaignScreen = ({ actions, referralCampaignsConfig }) => {
  const [sidePanelView, setSidePanelView] = useState({});
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [filters, setFilters] = useState({
    state: { active: false, inActive: false },
  });
  const [search, setSearch] = useState('');
  const [sortByAesc, setSortByAesc] = useState(false);
  const { data, isLoading, isLoaded } = referralCampaignsConfig || {};
  const { campaigns } = data || {};
  const modifiedCampaigns = useMemo(() => getModifiedCampaigns(campaigns), [campaigns]);

  useEffect(() => {
    actions.getConfig(REFERRAL_CAMPAIGNS);
  }, [actions]);

  useEffect(() => {
    if (isLoaded) {
      setFilteredCampaigns(campaigns);
    }
  }, [isLoaded, campaigns]);

  const multiPropsFilter = (campaigns, filters) => {
    const filterKeys = Object.keys(filters);

    return campaigns.filter(campaign => {
      return filterKeys.every(key => {
        if (!filters[key].length) return true;

        return filters[key].includes(campaign[key]);
      });
    });
  };

  const getfiltersCollected = () => {
    const collectedTrueKeys = {
      state: [],
    };
    const { state } = filters;
    for (let stateKey in state) {
      if (state[stateKey]) collectedTrueKeys.state.push(stateKey);
    }
    return collectedTrueKeys;
  };

  useEffect(() => {
    if (isLoaded) {
      const filtersCollected = getfiltersCollected();
      const campaignsAfterFiltering = multiPropsFilter(
        modifiedCampaigns,
        filtersCollected,
      );
      if (search) {
        const searchedCampaigns = campaignsAfterFiltering.filter(campaign => {
          return campaign.name.toLowerCase().includes(search);
        });
        setFilteredCampaigns(searchedCampaigns);
      } else {
        setFilteredCampaigns(campaignsAfterFiltering);
      }
    }
  }, [filters, isLoaded]);

  const openSideDrawer = (view, props, title) => {
    setSidePanelView({
      view,
      props,
      title,
    });
  };

  const handleLinkClick = campaignName => {
    window.open(
      `https://www.cuemath.com/gift/referral/IW2Q/?override=${campaignName}`,
      '_blank',
    );
  };

  const handleCampaignNameFilter = value => {
    setSearch(value);
    const filteredCampaigns = multiPropsFilter(modifiedCampaigns, getfiltersCollected());
    const searchedCampaigns = filteredCampaigns.filter(campaign => {
      return campaign.name.toLowerCase().includes(value);
    });
    setFilteredCampaigns(searchedCampaigns);
  };

  const allFilterClickListener = (name, filterProp) => {
    setFilters({
      ...filters,
      [filterProp]: { ...filters[filterProp], [name]: !filters[filterProp][name] },
    });
  };

  const sortByOrder = sortByAescending => {
    let sort;
    if (sortByAescending) {
      sort = [
        ...filteredCampaigns.sort(
          (a, b) => +new Date(b?.expiration_date) - +new Date(a?.expiration_date),
        ),
      ];
    } else {
      sort = [
        ...filteredCampaigns.sort(
          (a, b) => +new Date(a?.expiration_date) - +new Date(b?.expiration_date),
        ),
      ];
    }
    setFilteredCampaigns(sort);
    setSortByAesc(!sortByAesc);
  };

  const isFiltersApplied = getfiltersCollected().state.length;

  return (
    <Root id="manual-portal-root">
      <AppProvider state={{ setSidePanelView, sidePanelView }}>
        <Drawer />
        <Heading>Referral Campaigns</Heading>
        <Separator height={16} transparent />
        <Table
          headerCell={[
            {
              title: 'Expiry Date',
              onClick: () => sortByOrder(sortByAesc),
            },
            { title: 'Incentives' },
            {
              title: 'Active',
              filterApplied: isFiltersApplied,
              filterComponent: (
                <Row
                  justifyContent="space-between"
                  style={{
                    background: 'white',
                    borderRadius: '8px',
                    border: '1px solid #B9CDDC',
                    padding: '4px 24px',
                  }}
                >
                  <Checkbox
                    label="Active"
                    name="active"
                    onChange={(e, data) => allFilterClickListener(data.name, 'state')}
                    checked={filters.state.active}
                  />
                  <Checkbox
                    label="Inactive"
                    name="inActive"
                    onChange={(e, data) => allFilterClickListener(data.name, 'state')}
                    checked={filters.state.inActive}
                  />
                </Row>
              ),
              showFilters: true,
            },
            { title: 'Life (In Days)' },
            {
              title: 'Name',
              filterApplied: search?.length,
              filterComponent: (
                <Input
                  placeholder="Filter"
                  name="name"
                  value={search}
                  onChange={e => handleCampaignNameFilter(e.target.value)}
                />
              ),
              showFilters: true,
            },
            { title: 'Qualifying Criteria' },
            { title: 'Qualifying Criteria 2' },
            { title: 'Start Date' },
            { title: 'Template' },
            { title: 'Tracked Events' },
            { title: 'Tracked Progress Events' },
            { title: 'Validity Criteria' },
            { title: 'Weighted Experiments' },
            { title: 'Sample Link' },
          ]}
          showInfo={false}
          showLoader={isLoading}
        >
          {filteredCampaigns?.map(campaign => {
            const {
              expiration_date: expirationDate,
              incentives,
              is_active: isActive,
              lifetime,
              name,
              qualifying_criteria: qualifyingCriteria,
              qualifying_criteria_2: qualifyingCriteria2,
              start_date: startDate,
              template,
              tracked_events: trackedEvents,
              tracked_progress_events: trackedProgressEvents,
              validity_criteria: validityCriteria,
              weighted_experiments: weightedExperiments,
            } = campaign || {};

            const isCampaignActive = isActive ? 'true' : 'false';
            const lifetimeInDays = lifetime / (60 * 60 * 24);
            const expiryDate = +new Date(expirationDate) / 1000;
            const formattedStartDate = +new Date(startDate) / 1000;
            const activeCampaign =
              isActive && +new Date(expirationDate) / 1000 >= Date.now() / 1000;
            const textColor = activeCampaign ? COLORS.DARK_GREEN : COLORS.RED;

            return (
              <TableRow key={name} error={!activeCampaign} positive={activeCampaign}>
                <TableCell color={textColor} width={1}>
                  {getFormatedDate(expiryDate)}
                </TableCell>
                <TableInfoCell
                  width={1}
                  onClick={() =>
                    openSideDrawer('referral-incentives', incentives, 'Incentives')
                  }
                />
                <TableCell color={textColor} width={1}>
                  {isCampaignActive}
                </TableCell>
                <TableCell color={textColor} width={1}>
                  {lifetimeInDays}
                </TableCell>
                <TableCell color={textColor} width={2}>
                  {name}
                </TableCell>
                <TableInfoCell
                  width={1}
                  onClick={() =>
                    openSideDrawer(
                      'referral-qualifying-criteria',
                      qualifyingCriteria,
                      'Qualifying Criteria',
                    )
                  }
                />
                <TableInfoCell
                  width={1}
                  onClick={() =>
                    openSideDrawer(
                      'referral-qualifying-criteria-2',
                      qualifyingCriteria2,
                      'Qualifying Criteria 2',
                    )
                  }
                />
                <TableCell color={textColor} width={1}>
                  {getFormatedDate(formattedStartDate)}
                </TableCell>
                <TableInfoCell
                  width={1}
                  onClick={() =>
                    openSideDrawer('referral-template', template, 'Templates')
                  }
                />
                <TableInfoCell
                  width={1}
                  onClick={() =>
                    openSideDrawer(
                      'referral-tracked-events',
                      trackedEvents,
                      'Tracked Events',
                    )
                  }
                />
                <TableInfoCell
                  width={1}
                  onClick={() =>
                    openSideDrawer(
                      'referral-tracked-progress-events',
                      trackedProgressEvents,
                      'Tracked Progress Events',
                    )
                  }
                />
                <TableInfoCell
                  width={1}
                  onClick={() =>
                    openSideDrawer(
                      'referral-validity-criteria',
                      validityCriteria,
                      'Validity Criteria',
                    )
                  }
                />
                <TableInfoCell
                  width={1}
                  onClick={() =>
                    openSideDrawer(
                      'referral-weighted-experiments',
                      weightedExperiments,
                      'Experiments Percentage Wise',
                    )
                  }
                />
                <TableCell
                  color={textColor}
                  width={1}
                  onClick={() => handleLinkClick(name)}
                >
                  link
                </TableCell>
              </TableRow>
            );
          })}
        </Table>
      </AppProvider>
    </Root>
  );
};

export default connect(
  ({ configs }) => ({
    referralCampaignsConfig: store.get(configs, REFERRAL_CAMPAIGNS),
  }),
  dispatch => ({
    actions: bindActionCreators({ getConfig }, dispatch),
  }),
)(ReferralCampaignScreen);
